import { getCoverThumbUrl } from '../../utils/coverUrl';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, FileX, Loader2, RotateCcw, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getCoverUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=120&q=60';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${window.location.origin}${url}`;
};

/**
 * BookFileVerifier — Admin tool that scans every book in the catalog and
 * reports any whose readable PDF/EPUB file is missing or partial. Each
 * problem row shows a one-click "Fix in BookDetails" link that takes the
 * admin straight to the book page where the new "Re-upload Book File"
 * button can repair it.
 */
const BookFileVerifier = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runScan = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/books/admin/verify-files`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not run the verification scan');
    } finally {
      setLoading(false);
    }
  };

  const totalProblems = report
    ? (report.broken_count || 0) + (report.missing_file_count || 0)
    : 0;

  return (
    <div
      className="bg-white/5 rounded-xl border border-white/10 p-6"
      data-testid="book-file-verifier-section"
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center">
          <ShieldCheck className="h-6 w-6 text-emerald-400 mr-2" />
          <div>
            <h2 className="text-xl font-bold text-white">Verify Book Files</h2>
            <p className="text-xs text-gray-400 mt-1">
              Scan every book and surface any whose readable PDF/EPUB is missing or partial.
            </p>
          </div>
        </div>
        <Button
          onClick={runScan}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="run-verify-files-btn"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Scanning…
            </>
          ) : (
            <>
              <ShieldCheck className="mr-2 h-4 w-4" /> {report ? 'Re-scan' : 'Verify all book files'}
            </>
          )}
        </Button>
      </div>

      {error && (
        <div
          className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-200"
          data-testid="verify-error"
        >
          {error}
        </div>
      )}

      {!report && !loading && !error && (
        <p className="text-sm text-gray-500 italic">
          Click <span className="text-emerald-300">Verify all book files</span> to start a scan.
          The check is read-only and takes a few seconds even on large catalogs.
        </p>
      )}

      {report && (
        <div data-testid="verify-report">
          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Tile
              label="Total books"
              value={report.total_books}
              tone="neutral"
              testId="tile-total"
            />
            <Tile
              label="Healthy"
              value={report.healthy_count}
              tone="ok"
              testId="tile-healthy"
            />
            <Tile
              label="Broken"
              value={report.broken_count}
              tone={report.broken_count ? 'bad' : 'neutral'}
              testId="tile-broken"
            />
            <Tile
              label="Missing file"
              value={report.missing_file_count}
              tone={report.missing_file_count ? 'warn' : 'neutral'}
              testId="tile-missing"
            />
          </div>

          {totalProblems === 0 && (
            <div
              className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-emerald-200 text-sm flex items-center gap-2"
              data-testid="verify-all-clear"
            >
              <ShieldCheck className="h-5 w-5" />
              All books look good — every file scanned passed integrity checks.
            </div>
          )}

          {/* Broken list */}
          {report.broken?.length > 0 && (
            <ProblemList
              title="Broken files (need re-upload)"
              icon={<AlertTriangle className="h-4 w-4 text-red-400" />}
              rows={report.broken}
              navigate={navigate}
              testId="verify-broken-list"
              tone="red"
            />
          )}

          {/* Missing file list */}
          {report.missing_file?.length > 0 && (
            <ProblemList
              title="Books with no file uploaded"
              icon={<FileX className="h-4 w-4 text-amber-400" />}
              rows={report.missing_file}
              navigate={navigate}
              testId="verify-missing-list"
              tone="amber"
            />
          )}
        </div>
      )}
    </div>
  );
};

const Tile = ({ label, value, tone, testId }) => {
  const toneClass = {
    ok: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200',
    bad: 'bg-red-500/10 border-red-500/30 text-red-200',
    warn: 'bg-amber-500/10 border-amber-500/30 text-amber-200',
    neutral: 'bg-white/5 border-white/10 text-gray-200',
  }[tone] || 'bg-white/5 border-white/10';
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneClass}`} data-testid={testId}>
      <p className="text-2xl font-bold">{value ?? 0}</p>
      <p className="text-[11px] uppercase tracking-wider opacity-80">{label}</p>
    </div>
  );
};

const ProblemList = ({ title, icon, rows, navigate, testId, tone }) => {
  const headBg = tone === 'red' ? 'bg-red-500/10' : 'bg-amber-500/10';
  return (
    <div className="mt-4" data-testid={testId}>
      <h3 className={`text-sm font-semibold text-white mb-2 flex items-center gap-2 ${headBg} rounded-md px-3 py-1.5`}>
        {icon}
        {title}
        <span className="ml-auto text-xs opacity-70">{rows.length}</span>
      </h3>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.book_id}
            className="bg-black/30 rounded-lg p-3 flex items-center gap-3 hover:bg-black/40 transition-colors"
            data-testid={`problem-row-${row.book_id}`}
          >
            <img
              src={getCoverThumbUrl(row.cover_image_url)}
              alt=""
              className="w-10 h-14 object-cover rounded flex-shrink-0"
              loading="lazy"
            />
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-medium truncate">{row.title || 'Untitled'}</p>
              <p className="text-gray-400 text-xs truncate">by {row.author || 'Unknown'}</p>
              <p className="text-red-300 text-[11px] italic truncate" title={row.reason}>
                {row.reason}
              </p>
            </div>
            <Button
              onClick={() => navigate(`/book/${row.book_id}`)}
              variant="secondary"
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs flex-shrink-0"
              data-testid={`fix-book-${row.book_id}-btn`}
            >
              <RotateCcw className="mr-1 h-3 w-3" />
              Fix
              <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BookFileVerifier;
